import { Box, Button, Stack, Typography } from "@mui/joy";
import { motion, useInView } from "framer-motion";
import { useContext, useRef } from "react";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { Section, Container, COLORS, GlowOrbs } from "../shared/ui";
import { LayoutContext } from "../context";

const promises = [
  "Дата выдачи зафиксирована в договоре",
  "Опоздали на день — вернём 100% стоимости",
  "Без скрытых условий и мелкого шрифта",
];

export default function Guarantee({ onShowOrder }) {
  const { setQuickOrderCalcAmount, setShowQuickOrder } = useContext(LayoutContext);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const handleQuickOrder = () => {
    setQuickOrderCalcAmount(null);
    setShowQuickOrder(true);
  };

  return (
    <Section
      id="guarantee"
      dark
      py={{ xs: 10, md: 14 }}
      sx={{
        background: `linear-gradient(135deg, ${COLORS.dark} 0%, #0f1630 100%)`,
      }}
    >
      <GlowOrbs />

      <Container maxWidth={1080}>
        <Box
          ref={ref}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" },
            gap: { xs: 4, md: 6 },
            alignItems: "center",
          }}
        >
          {/* Огромная «3» */}
          <Box sx={{ position: "relative", textAlign: { xs: "center", md: "left" } }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.7 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.9, ease: [0.34, 1.56, 0.64, 1] }}
            >
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: "12rem", sm: "16rem", md: "18rem" },
                  lineHeight: 0.85,
                  letterSpacing: "-10px",
                  background: "linear-gradient(135deg, #818cf8 0%, #c084fc 50%, #6366f1 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 0 120px rgba(99,102,241,0.4)",
                  filter: "drop-shadow(0 20px 60px rgba(99,102,241,0.3))",
                  display: "inline-block",
                }}
              >
                3
              </Typography>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Typography
                sx={{
                  color: COLORS.brandLight,
                  fontFamily: "monospace",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  mt: 1,
                }}
              >
                рабочих дня. максимум.
              </Typography>
            </motion.div>
          </Box>

          {/* Текст обещания */}
          <Box>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 2 }}>
                <ShieldRoundedIcon sx={{ color: COLORS.brandLight, fontSize: 22 }} />
                <Typography
                  level="body-xs"
                  sx={{
                    color: COLORS.brandLight,
                    textTransform: "uppercase",
                    letterSpacing: "0.16em",
                    fontWeight: 700,
                  }}
                >
                  Гарантия сроков
                </Typography>
              </Stack>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.65, delay: 0.3 }}
            >
              <Typography
                level="h2"
                sx={{
                  color: "#fff",
                  fontWeight: 900,
                  fontSize: { xs: "1.9rem", md: "2.6rem" },
                  letterSpacing: "-1.2px",
                  lineHeight: 1.1,
                  mb: 2.5,
                }}
              >
                Три дня на обмерный план.
                <br />
                <Box component="span" sx={{ color: "#c084fc" }}>
                  Иначе — бесплатно.
                </Box>
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Typography
                level="body-lg"
                sx={{
                  color: COLORS.textDim,
                  mb: 3,
                  lineHeight: 1.65,
                  maxWidth: 520,
                }}
              >
                Мы уверены в своих сроках настолько, что прописываем дату выдачи в договоре. Опоздали — возвращаем 100% стоимости замера.
              </Typography>
            </motion.div>

            {/* Список обещаний */}
            <Stack spacing={1.25} sx={{ mb: 4 }}>
              {promises.map((text, i) => (
                <motion.div
                  key={text}
                  initial={{ opacity: 0, x: -10 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.5 + i * 0.1 }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <CheckCircleRoundedIcon sx={{ color: "#34d399", fontSize: 20 }} />
                    <Typography level="body-md" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 500 }}>
                      {text}
                    </Typography>
                  </Stack>
                </motion.div>
              ))}
            </Stack>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} style={{ display: "inline-block" }}>
                <Button
                  size="lg"
                  onClick={onShowOrder}
                  onClick={handleQuickOrder}
                  sx={{
                    fontWeight: 800,
                    px: 4,
                    py: 1.5,
                    borderRadius: "14px",
                    background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                    boxShadow: "0 10px 36px rgba(99,102,241,0.5)",
                    "&:hover": {
                      boxShadow: "0 14px 48px rgba(99,102,241,0.7)",
                      background: `linear-gradient(135deg, ${COLORS.brand}, ${COLORS.brand2})`,
                    },
                  }}
                >
                  Заказать замер с гарантией
                </Button>
              </motion.div>
            </motion.div>
          </Box>
        </Box>
      </Container>
    </Section>
  );
}